import { supabase } from '@/integrations/supabase/client';
import { User, UserRole } from '@/types/userRole';

// Convert Supabase user and profile to our User type
export const convertSupabaseUser = (supabaseUser: any, profile: any): User => {
  return {
    id: supabaseUser.id,
    name: profile ? `${profile.first_name} ${profile.last_name}`.trim() : supabaseUser.email?.split('@')[0] || 'User',
    email: supabaseUser.email || '',
    role: profile?.role || 'user',
    createdAt: new Date(supabaseUser.created_at),
    isMasterAdmin: profile?.role === 'masterAdmin',
    isAdmin: ['admin', 'superadmin', 'masterAdmin'].includes(profile?.role),
    isSuperAdmin: ['superadmin', 'masterAdmin'].includes(profile?.role)
  };
};

// Login user with Supabase
export const loginUser = async (email: string, password: string): Promise<{ user: User, token: string }> => {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password
  });

  if (error) {
    throw new Error(error.message);
  }

  if (!data.user || !data.session) {
    throw new Error('Login failed');
  }

  // Fetch user profile
  const { data: profile } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', data.user.id)
    .single();

  const user = convertSupabaseUser(data.user, profile);
  
  return {
    user,
    token: data.session.access_token
  };
};

// Register user with Supabase
export const registerUser = async (userData: {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  role?: string;
}): Promise<{ user: User, token: string }> => {
  const { data, error } = await supabase.auth.signUp({
    email: userData.email,
    password: userData.password,
    options: {
      emailRedirectTo: `${window.location.origin}/`,
      data: {
        first_name: userData.firstName,
        last_name: userData.lastName,
        role: userData.role || 'user'
      }
    }
  });

  if (error) {
    throw new Error(error.message);
  }

  if (!data.user || !data.session) {
    throw new Error('Registration failed');
  }

  // The profile will be created automatically by the trigger
  const user = convertSupabaseUser(data.user, {
    first_name: userData.firstName,
    last_name: userData.lastName,
    role: userData.role || 'user'
  });

  return {
    user,
    token: data.session.access_token
  };
};

// Logout user
export const logoutUser = async (): Promise<void> => {
  const { error } = await supabase.auth.signOut();
  if (error) {
    console.error('Logout error:', error);
  }
};

// Get current user
export const getCurrentUser = (): User | null => {
  // This should be handled by the SupabaseAuthContext
  return null;
};

// Check if user is authenticated
export const isAuthenticated = (): boolean => {
  // This should be handled by the SupabaseAuthContext
  return false;
};

// Role checking functions (backwards compatibility)
export const isAdmin = (role?: string): boolean => {
  return ['admin', 'superadmin', 'masterAdmin'].includes(role || '');
};

export const isSuperAdmin = (role?: string): boolean => {
  return ['superadmin', 'masterAdmin'].includes(role || '');
};

export const isMasterAdmin = (role?: string): boolean => {
  return role === 'masterAdmin';
};

export const isFullAdmin = (role?: string): boolean => {
  return isAdmin(role);
};

// Placeholder functions for backwards compatibility
export const startFreeTrial = async (): Promise<{ success: boolean, trialDays: number }> => {
  return { success: true, trialDays: 14 };
};

export const submitSupportRequest = async (
  name: string,
  email: string,
  message: string,
  subject: string = "Support Request",
  priority: "low" | "medium" | "high" = "medium"
): Promise<void> => {
  console.log("Support request submitted:", { name, email, message, subject, priority });
};